import { BootcampBannerEntry, EntityName } from "@/schemaTypes";
import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { DescriptionTag } from "@/components/DescriptionTag";
import { SelectTagTooltip } from "@/shared/components/atoms/SelectTagTooltip/SelectTagTooltip";

interface DetailBannerProps {
  bootcamp: BootcampBannerEntry;
  startDateFormatted: string;
  showSelectTag?: boolean;
}

export const DetailBanner = ({
  bootcamp,
  startDateFormatted,
  showSelectTag = false,
}: DetailBannerProps) => {
  const { t } = useTranslation();
  const mastermindGradient =
    "radial-gradient(29.14% 58.14% at 28.86% 54.38%, #F5FFE6 0%, #FFF 100%)";
  const defaultGradient =
    "radial-gradient(29.14% 58.14% at 28.86% 54.38%, #E9F1FF 0%, #FFF 100%)";

  const backgroundImage = useMemo(() => {
    return `url(${
      bootcamp.release?.mentors?.[0]?.profilePicture ||
      "https://ui-avatars.com/api/?name=Mentor"
    })`;
  }, [bootcamp.release?.mentors]);

  return (
    <Flex
      flexDir={"column"}
      w="100%"
      gap={6}
      p={6}
      borderRadius={"6px"}
      border={"1px solid"}
      borderColor={
        bootcamp.parent?.bootcampType === EntityName.MastermindBootcamp
          ? "#c6daa7"
          : "primary.50"
      }
      bg={
        bootcamp.parent?.bootcampType === EntityName.MastermindBootcamp
          ? mastermindGradient
          : defaultGradient
      }
    >
      <Flex flexDir={"column"} gap={2}>
        <Flex alignItems={"center"} justifyContent={"space-between"} gap={2}>
          <Text
            fontFamily={"Poppins"}
            fontSize={"14px"}
            fontWeight={700}
            color={"secondary.300"}
            letterSpacing={"4%"}
            textTransform={"uppercase"}
          >
            {bootcamp.parent?.bootcampType === EntityName.MastermindBootcamp
              ? "MASTERMIND"
              : "BOOTCAMP"}
          </Text>
          {showSelectTag && <SelectTagTooltip iconOnly />}
        </Flex>
        <Heading as={"h5"}>{bootcamp?.release?.title}</Heading>

        <Flex flexDir={"column"} gap={4}>
          <Text fontSize={"14px"}>
            {bootcamp?.release?.description}
            <Text as={"span"} fontSize={"14px"} variant={"bold"}>
              {t(" Iniciamos el")} {startDateFormatted}.
            </Text>
          </Text>

          <Flex gap={2} alignItems={"center"}>
            <Box
              boxSize="32px"
              borderRadius="full"
              backgroundImage={backgroundImage}
              backgroundSize="cover"
              backgroundPosition="center top"
              backgroundRepeat="no-repeat"
              opacity={0.8}
            />
            <Text fontSize="14px" color="secondary.300" fontWeight={500}>
              {bootcamp.release?.mentors?.[0]?.name ?? ""}{" "}
              {bootcamp.release?.mentors?.[0]?.lastName ?? ""}
            </Text>
          </Flex>
          <Flex gap={2} alignItems={"center"}>
            {bootcamp.parent?.brand?.length &&
              bootcamp.parent?.brand?.length < 2 && (
                <DescriptionTag
                  bootcampType={bootcamp.parent?.bootcampType as string}
                >
                  {bootcamp.parent?.brand?.[0]?.name}
                </DescriptionTag>
              )}
            {bootcamp.parent?.subCategory?.length &&
              bootcamp.parent?.subCategory?.length < 2 && (
                <DescriptionTag
                  bootcampType={bootcamp.parent?.bootcampType as string}
                >
                  {bootcamp.parent?.subCategory?.[0]?.name}
                </DescriptionTag>
              )}
            <DescriptionTag
              bootcampType={bootcamp.parent?.bootcampType as string}
            >
              {bootcamp.parent?.level?.[0]?.name}
            </DescriptionTag>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
